<div class="relative mt-8 flex flex-auto flex-col items-center gap-8 px-6">
  <div class="w-full max-w-4xl flex flex-col gap-6">
    <!-- Header Page -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
      <div>
        <h1 class="text-2xl font-bold text-primary tracking-tight">Configuration & Contrôle des Jobs</h1>
        <p class="text-sm text-neutral-11 mt-1">
          Supervisez et mettez en pause ou reconfigurez en temps réel les files de traitement d'arrière-plan.
        </p>
      </div>
      <button mat-flat-button class="primary" (click)="loadConfigs()" [disabled]="loading()">
        <mat-icon class="mr-2">refresh</mat-icon> Rafraîchir
      </button>
    </div>

    @if (error()) {
      <div class="flex items-center gap-3 bg-red-3 border border-red-8 rounded-lg p-4 text-red-11 text-sm">
        <mat-icon class="text-red-9">error</mat-icon>
        <span>{{ error() }}</span>
      </div>
    }

    @if (loading()) {
      <div class="flex flex-col items-center justify-center py-12 gap-4">
        <mat-icon class="animate-spin text-primary size-8">refresh</mat-icon>
        <span class="text-sm text-neutral-11">Chargement des configurations...</span>
      </div>
    } @else {
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        @for (config of configs(); track config.queueName) {
          <div
            class="surface-default flex flex-col justify-between rounded-xl border border-neutral-a4 shadow-sm transition-all duration-200 hover:shadow-md"
          >
            <!-- Header Section -->
            <div class="flex items-start justify-between border-b border-neutral-a4 p-6 pb-4">
              <div class="flex gap-4">
                <div class="flex size-12 items-center justify-center rounded-lg bg-primary-3 text-primary">
                  <mat-icon class="size-6 flex items-center justify-center">{{
                    getQueueIcon(config.queueName)
                  }}</mat-icon>
                </div>
                <div class="flex flex-col">
                  <h3 class="font-bold text-neutral-12">{{ getQueueLabel(config.queueName) }}</h3>
                  <span class="text-xs font-mono text-neutral-10 mt-0.5">{{ config.queueName }}</span>
                </div>
              </div>

              <!-- Status and Pending Info -->
              <div class="flex flex-col items-end gap-2">
                <span
                  class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium"
                  [class.bg-green-3]="!config.paused"
                  [class.text-green-11]="!config.paused"
                  [class.bg-red-3]="config.paused"
                  [class.text-red-11]="config.paused"
                >
                  <span
                    class="size-1.5 rounded-full"
                    [class.bg-green-9]="!config.paused"
                    [class.bg-red-9]="config.paused"
                  ></span>
                  {{ config.paused ? 'En Pause' : 'Actif' }}
                </span>

                <span
                  class="inline-flex items-center gap-1.5 rounded-full bg-blue-3 px-2 py-0.5 text-xs font-semibold text-blue-11 border border-blue-4"
                  matTooltip="Nombre d'éléments en attente de traitement"
                >
                  <mat-icon class="!text-[12px] !w-3.5 !h-3.5 flex items-center justify-center"
                    >schedule</mat-icon
                  >
                  {{ getPendingCount(config.queueName) }} en attente
                </span>
              </div>
            </div>

            <!-- Body Section -->
            <div class="p-6 flex flex-col gap-6">
              <!-- Pause Control -->
              <div
                class="flex items-center justify-between bg-neutral-a2 rounded-lg p-4 border border-neutral-a4"
              >
                <div class="flex flex-col gap-0.5">
                  <span class="text-sm font-semibold text-neutral-12">Suspendre le traitement</span>
                  <span class="text-xs text-neutral-11"
                    >Arrête immédiatement l'exécution des nouveaux jobs.</span
                  >
                </div>
                <mat-slide-toggle
                  [checked]="config.paused"
                  (change)="togglePause(config)"
                  [disabled]="savingQueue() === config.queueName"
                >
                </mat-slide-toggle>
              </div>

              <!-- Configuration Fields -->
              <div class="grid grid-cols-3 gap-4">
                <div class="flex flex-col gap-1.5">
                  <label
                    [attr.for]="config.queueName + '-concurrency'"
                    class="text-xs text-neutral-11 font-semibold"
                    >Concurrence</label
                  >
                  <input
                    [id]="config.queueName + '-concurrency'"
                    type="number"
                    [(ngModel)]="config.concurrency"
                    min="1"
                    max="100"
                    class="w-full rounded-lg border border-neutral-a4 px-3 py-2 text-sm bg-surface font-semibold text-neutral-12 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                    [disabled]="savingQueue() === config.queueName"
                  />
                </div>

                <div class="flex flex-col gap-1.5">
                  <label
                    [attr.for]="config.queueName + '-maxAttempts'"
                    class="text-xs text-neutral-11 font-semibold"
                    >Essais Max</label
                  >
                  <input
                    [id]="config.queueName + '-maxAttempts'"
                    type="number"
                    [(ngModel)]="config.maxAttempts"
                    min="1"
                    max="20"
                    class="w-full rounded-lg border border-neutral-a4 px-3 py-2 text-sm bg-surface font-semibold text-neutral-12 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                    [disabled]="savingQueue() === config.queueName"
                  />
                </div>

                <div class="flex flex-col gap-1.5">
                  <label
                    [attr.for]="config.queueName + '-backoffDelay'"
                    class="text-xs text-neutral-11 font-semibold"
                    >Délai (ms)</label
                  >
                  <input
                    [id]="config.queueName + '-backoffDelay'"
                    type="number"
                    [(ngModel)]="config.backoffDelay"
                    min="0"
                    step="500"
                    class="w-full rounded-lg border border-neutral-a4 px-3 py-2 text-sm bg-surface font-semibold text-neutral-12 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                    [disabled]="savingQueue() === config.queueName"
                  />
                </div>
              </div>
            </div>
            <!-- Action Footer -->
            <div class="border-t border-neutral-a4 px-6 py-4 flex justify-end bg-neutral-a1 rounded-b-xl">
              <button
                mat-flat-button
                class="primary"
                (click)="saveConfig(config)"
                [disabled]="savingQueue() === config.queueName"
              >
                <mat-icon class="mr-2" [class.animate-spin]="savingQueue() === config.queueName">
                  {{ savingQueue() === config.queueName ? 'refresh' : 'save' }}
                </mat-icon>
                {{ savingQueue() === config.queueName ? 'Enregistrement...' : 'Sauvegarder' }}
              </button>
            </div>
          </div>
        }
      </div>
    }
  </div>
</div>
